/**
 * ProtocolStack — layered stack of the three agentic-commerce protocols.
 * Bespoke stacked SVG layers + a labelled legend; i18n; role="img" + <title>.
 *
 * @spec /doc/specs/frontend/marketing-layout-charter.md
 * @userStory US-EP033-047
 * @ddl none
 */

'use client';

import { useTranslations } from 'next-intl';
import { MotionReveal } from '@/components/marketing/ui/motion-reveal';
import { cn } from '@/lib/utils';

export interface ProtocolStackProps {
  namespace?: string;
  className?: string;
}

const LAYERS = ['l1', 'l2', 'l3'] as const;

export function ProtocolStack({ namespace = 'marketing.diagrams.protocolStack', className }: ProtocolStackProps) {
  const t = useTranslations(namespace);
  const titleId = 'protocol-stack-title';
  return (
    <MotionReveal direction="up" className="min-w-0">
      <div className={cn('glass-card min-w-0 p-6 lg:p-8', className)}>
        <h3 className="text-display text-lg font-semibold text-white lg:text-xl">{t('title')}</h3>
        <div className="mt-5 grid items-center gap-6 lg:grid-cols-[180px_1fr]">
          <svg
            viewBox="0 0 160 150"
            role="img"
            aria-labelledby={titleId}
            className="mx-auto w-full max-w-[180px]"
          >
            <title id={titleId}>{t('title')}</title>
            {LAYERS.map((layer, i) => {
              const y = 12 + i * 44;
              const inset = i * 10;
              return (
                <g key={layer}>
                  <rect
                    x={inset}
                    y={y}
                    width={160 - inset * 2}
                    height="36"
                    rx="8"
                    fill="url(#protocol-grad)"
                    opacity={0.35 + i * 0.22}
                    stroke="#1aabff"
                    strokeOpacity="0.4"
                  />
                  <text
                    x="80"
                    y={y + 23}
                    textAnchor="middle"
                    fill="#ffffff"
                    fontSize="14"
                    fontWeight="700"
                    fontFamily="var(--font-comfortaa)"
                  >
                    {t(`layers.${layer}.name`)}
                  </text>
                </g>
              );
            })}
            <defs>
              <linearGradient id="protocol-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#1aabff" />
                <stop offset="100%" stopColor="#0d8edb" />
              </linearGradient>
            </defs>
          </svg>
          <dl className="space-y-4">
            {LAYERS.map((layer) => (
              <div key={layer} className="border-l-2 border-cyan-500/30 pl-4">
                <dt className="text-display text-base font-semibold text-cyan-300">
                  {t(`layers.${layer}.name`)}
                  <span className="ml-2 text-xs font-normal text-white/50">
                    {t(`layers.${layer}.by`)}
                  </span>
                </dt>
                <dd className="mt-1 text-sm leading-relaxed text-white/70">
                  {t(`layers.${layer}.body`)}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </MotionReveal>
  );
}
